import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";

type AdsStatus = "loading" | "ready" | "error";

interface AdsConfig {
  video:
    | { enabled: false }
    | {
        enabled: true;
        networkId: string;
        adUnit: string;
        size: string;
      };
  display:
    | { enabled: false }
    | {
        enabled: true;
        networkId: string;
        slateAdUnit: string;
        bugAdUnit: string;
      };
}

interface AdsContextValue {
  imaStatus: AdsStatus;
  ima: object | null;
  gptStatus: AdsStatus;
  gpt: typeof window.googletag | null;
}

const platformConfig: Record<"allstar" | "hltv" | "none", AdsConfig> = {
  allstar: {
    video: {
      enabled: true,
      networkId: "23325224050",
      adUnit: "player_video_preroll",
      size: "1280x720",
    },
    display: { enabled: false },
  },
  hltv: {
    video: { enabled: false },
    display: {
      enabled: true,
      networkId: "23325224050",
      slateAdUnit: "player_pause_end_slate",
      bugAdUnit: "player_top_left_bug",
    },
  },
  none: {
    video: { enabled: false },
    display: { enabled: false },
  },
};

const getPlatformConfig = (
  platform: string,
  queryParams?: URLSearchParams,
): AdsConfig => {
  switch (platform) {
    case "ALLSTAR":
      return platformConfig["allstar"];
    case "HLTV":
    case "HLTV.ORG":
    case "CROM, INC.":
    case "CROM,INC": {
      const baseConfig = platformConfig["hltv"];

      if (queryParams) {
        const videoNetworkId = queryParams.get("networkId");
        const videoAdUnit = queryParams.get("adUnit");
        const videoSize = queryParams.get("size");

        if (videoNetworkId && videoAdUnit && videoSize) {
          return {
            ...baseConfig,
            video: {
              enabled: true,
              networkId: videoNetworkId,
              adUnit: videoAdUnit,
              size: videoSize,
            },
          };
        }
      }

      return baseConfig;
    }
    default:
      return platformConfig["none"];
  }
};

const AdsContext = createContext<AdsContextValue>({
  imaStatus: "loading",
  ima: null,
  gptStatus: "loading",
  gpt: null,
});

export const AdsProvider = ({ children }: { children: ReactNode }) => {
  const [imaStatus, setImaStatus] = useState<AdsStatus>("loading");
  const [gptStatus, setGptStatus] = useState<AdsStatus>("loading");
  const [ima, setIma] = useState<object | null>(null);
  const [gpt, setGpt] = useState<typeof window.googletag | null>(null);

  useEffect(() => {
    if (window.google?.ima) {
      setImaStatus("ready");
      setIma(window.google.ima);
    }

    if (window.googletag) {
      setGptStatus("ready");
      setGpt(window.googletag);
    }

    const imaScript = document.createElement("script");
    imaScript.src = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
    imaScript.async = true;
    document.head.appendChild(imaScript);

    const gptScript = document.createElement("script");
    gptScript.src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
    gptScript.async = true;
    document.head.appendChild(gptScript);

    let checks = 0;
    const checkInterval = setInterval(() => {
      if (imaStatus === "loading" && window.google?.ima) {
        setImaStatus("ready");
        setIma(window.google.ima);
      }

      if (gptStatus === "loading" && window.googletag) {
        const gt = window.googletag;

        gt.cmd.push(function () {
          gt.enableServices();
        });
        setGptStatus("ready");
        setGpt(window.googletag);
      }

      // Timeout after 2 seconds (200 checks * 10ms)
      if ((imaStatus === "ready" && gptStatus === "ready") || checks > 200) {
        clearInterval(checkInterval);
        if (imaStatus === "loading") setImaStatus("error");
        if (gptStatus === "loading") setGptStatus("error");
      }

      checks++;
    }, 10);

    return () => clearInterval(checkInterval);
  }, [imaStatus, gptStatus]);

  return (
    <AdsContext.Provider value={{ imaStatus, ima, gptStatus, gpt }}>
      {children}
    </AdsContext.Provider>
  );
};

export function useAds(): AdsContextValue & { config: AdsConfig } {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform") ?? "ALLSTAR";

  const context = useContext(AdsContext);
  if (!context) {
    throw new Error("useAds must be used within an AdsProvider");
  }
  return { ...context, config: getPlatformConfig(platform, params) };
}
